import React, { useRef } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import DescriptionForm from "../../WorkOrder/Forms/DescriptionForm";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { updateWorkOrderApi } from "../../../services/api/workOrder";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { useMutation } from "@tanstack/react-query";
import { useSectionChanges } from "../../../hooks/useSectionChanges";

const sanitizeEditorHtml = (html) => {
    if (!html) return "";
    const text = html.replace(/<p>(\s|&nbsp;|<br>)<\/p>/gi, "").trim();
    return text === "" ? "" : html;
};

// Quill rewrites an empty field to "<p><br></p>" as soon as it mounts, which is not
// an edit — compare what would actually be submitted instead of the raw markup.
const normalizeServiceDescription = (serviceDescription) => ({
    description: sanitizeEditorHtml(serviceDescription?.description),
    confidentialInformation: sanitizeEditorHtml(serviceDescription?.confidentialInformation),
});

const EditServiceDescriptionModal = ({ open, close, workOrderData }) => {
    const dispatch = useDispatch();
    const { updateWorkOrder } = useUpdateWorkOrder();
    const {
        isPending: isUpdatingLoading,
        error: isUpdatingError,
        mutateAsync: updateWorkOrderApiMutation,
    } = useMutation({
        mutationFn: updateWorkOrderApi,
        onSuccess: (data) => { },
    });

    const workOrder = useSelector(
        (state) => state.workOrder
    );
    const { hasChanges, onHydrate } = useSectionChanges(
        workOrder.summary.serviceDescription,
        normalizeServiceDescription,
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        const updatedPayload = {
            id: workOrder.workOrderDetails.id,
            description: sanitizeEditorHtml(workOrder.summary.serviceDescription?.description),
            confidentialInformation: sanitizeEditorHtml(workOrder.summary.serviceDescription?.confidentialInformation || ""),
        }
        try {
            await updateWorkOrderApiMutation({
                ...updatedPayload
            });

            dispatch(
                showToast({
                    message: "Service Description updated successfully!",
                    type: "success",
                })
            );

            updateWorkOrder();
            close(false);
        } catch (error) {
            dispatch(
                showToast({
                    message: "Failed to update Service Description.",
                    type: "error",
                })
            );
        }
    };

    return (
        <BaseModal className="sm:-top-[200px] overflow-auto" open={open} close={close}>
            <div className="py-3 sm:py-7 px-4 sm:px-12 w-full sm:w-[800px] lg:w-[1000px] xl:w-[1400px]">
                <h6 className="text-dark font-normal border-b pb-1">
                    Edit Service Description
                </h6>

                <div className="mb-2 mt-3">
                    <DescriptionForm isEditMode={true} workOrderData={workOrderData} onHydrate={onHydrate} />
                </div>
            </div>

            <div
                className="d-flex content-center bg-white py-6"
                style={{
                    boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
                }}
            >
                <BaseButton
                    className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "16px" }}
                    onClick={() => close(false)}
                >
                    Cancel
                </BaseButton>

                <BaseButton
                    className="px-[12px] py-[5px] min-w-32"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!hasChanges}
                >
                    Save Changes
                </BaseButton>
            </div>
        </BaseModal>
    );
};

export default EditServiceDescriptionModal;
